import { FormEvent, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  type FormFieldDef,
  type FormSectionDef,
  readFormSchema,
} from "../modules/forms/formSchema";

type PublicForm = {
  id: string;
  name: string;
  description: string | null;
  schemaJson: Record<string, unknown>;
  publicToken: string;
};

export function PublicFormPage() {
  const { token } = useParams<{ token: string }>();
  const [form, setForm] = useState<PublicForm | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    void axios
      .get<{ item: PublicForm }>(`/api/v1/public/forms/${token}`)
      .then((res) => setForm(res.data.item))
      .catch(() => setLoadError("הטופס לא נמצא או שאינו זמין"));
  }, [token]);

  const schema = useMemo(
    () => readFormSchema(form?.schemaJson),
    [form?.schemaJson],
  );

  const sections: FormSectionDef[] = schema.sections;
  const allFields: FormFieldDef[] = sections.flatMap((s) => s.fields || []);

  const setVal = (id: string, v: string) => {
    setValues((prev) => ({ ...prev, [id]: v }));
  };

  const validate = (): string | null => {
    for (const f of allFields) {
      if (f.required) {
        const v = values[f.id];
        if (v === undefined || v === null || String(v).trim() === "") {
          return `שדה חובה חסר: ${f.label}`;
        }
      }
      if (f.type === "numeric" && values[f.id] !== undefined && values[f.id] !== "") {
        if (Number.isNaN(Number(values[f.id]))) {
          return `שדה "${f.label}" חייב להיות מספרי`;
        }
      }
    }
    return null;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    const data: Record<string, unknown> = {
      answers: allFields.map((f) => ({
        id: f.id,
        label: f.label,
        type: f.type,
        value: values[f.id] ?? null,
      })),
    };
    for (const f of allFields) {
      data[f.id] = values[f.id] ?? null;
    }
    void axios
      .post(`/api/v1/public/forms/${token}/submit`, { data })
      .then(() => setDone(true))
      .catch(() => setError("שליחה נכשלה"))
      .finally(() => setSubmitting(false));
  };

  if (loadError) {
    return (
      <main className="public-form public-form--centered">
        <div className="public-form__panel">
          <h1>הטופס אינו זמין כעת</h1>
          <p>{loadError}</p>
        </div>
      </main>
    );
  }

  if (!form) {
    return (
      <main className="public-form public-form--centered">
        <p>טוען טופס…</p>
      </main>
    );
  }

  if (done) {
    return (
      <main className="public-form public-form--centered">
        <div className="public-form__panel">
          <h1 className="public-form__success">תודה!</h1>
          <p>ההגשה התקבלה בהצלחה.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="public-form">
      <div className="public-form__panel">
        {schema.logoUrl ? (
          <div className="public-form__logo">
            <img src={schema.logoUrl} alt="" />
          </div>
        ) : null}
        <h1>{form.name}</h1>
        {form.description ? (
          <p className="public-form__description">{form.description}</p>
        ) : null}

        <form onSubmit={handleSubmit}>
          {sections.map((section) => (
            <section key={section.id || section.title} className="public-form__section">
              {section.title ? <h2>{section.title}</h2> : null}
              {(section.fields || []).map((f) => {
                if (f.type === "dropdown") {
                  return (
                    <label key={f.id} className="form-field">
                      <span>
                        {f.label}
                        {f.required ? " *" : ""}
                      </span>
                      <select
                        required={!!f.required}
                        value={values[f.id] ?? ""}
                        onChange={(ev) => setVal(f.id, ev.target.value)}
                      >
                        <option value="">{f.placeholder || "בחר..."}</option>
                        {(f.options || []).map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                }
                if (f.type === "numeric") {
                  return (
                    <label key={f.id} className="form-field">
                      <span>
                        {f.label}
                        {f.required ? " *" : ""}
                      </span>
                      <input
                        inputMode="decimal"
                        required={!!f.required}
                        placeholder={f.placeholder || ""}
                        value={values[f.id] ?? ""}
                        onChange={(ev) => {
                          const v = ev.target.value.replace(/[^-\d.]/g, "");
                          setVal(f.id, v);
                        }}
                      />
                    </label>
                  );
                }
                return (
                  <label key={f.id} className="form-field">
                    <span>
                      {f.label}
                      {f.required ? " *" : ""}
                    </span>
                    <input
                      required={!!f.required}
                      placeholder={f.placeholder || ""}
                      value={values[f.id] ?? ""}
                      onChange={(ev) => setVal(f.id, ev.target.value)}
                    />
                  </label>
                );
              })}
            </section>
          ))}

          {sections.length === 0 || allFields.length === 0 ? (
            <p className="public-form__empty">אין שדות בטופס זה.</p>
          ) : null}

          {error ? <p className="form-error">{error}</p> : null}

          <button
            type="submit"
            className="btn btn--primary"
            disabled={submitting || allFields.length === 0}
          >
            {submitting ? "שולח..." : "שלח טופס"}
          </button>
        </form>
      </div>
    </main>
  );
}
